import React, { useState } from 'react';
import './OneRepMaxTool.css'; // Import the new CSS file

const formulas = {
  epley: (weight, reps) => weight * (1 + reps / 30),
  brzycki: (weight, reps) => weight * (36 / (37 - reps)),
};

const calculateBase1RM = (weight, reps, formula) => {
  if (formula === 'epley') return weight * (1 + reps / 30);
  if (formula === 'brzycki') return weight * (36 / (37 - reps));
  return 0;
};

const estimateForReps = (n, oneRM, formula) => {
  if (n === 0) return 0; // Avoid division by zero
  if (n < 1) return 0; // No reps means no max
  if (n === 1) return oneRM; // 1 rep max is just the oneRM
  if (formula === 'epley') return oneRM / (1 + n / 30);
  if (formula === 'brzycki') return oneRM * (37 - n) / 36;
  return 0;
};

function OneRepMaxTool() {
  const [weight, setWeight] = useState(100);
  const [reps, setReps] = useState(5);
  const [formula, setFormula] = useState('epley');
  const [maxRepsToShow, setMaxRepsToShow] = useState(12);
  const [showHistory, setShowHistory] = useState(false);
  const [showFormulaInfo, setShowFormulaInfo] = useState(false);

  const oneRM = calculateBase1RM(weight, reps, formula);

  return (
    <div className="onerepmax-tool-container fogged-card">
      <h1>🧮 One Rep Max Calculator & Multi-Rep Estimator</h1>

      {/* History Toggle */}
      <div className="onerepmax-history-toggle">
        <button onClick={() => setShowHistory(!showHistory)} className="onerepmax-toggle-button">
          {showHistory ? 'Hide Explanation' : 'What are One Rep Maxes?'}
        </button>
        {showHistory && (
          <div className="onerepmax-history-content">
            <p>The practice of estimating a one-rep max has evolved from empirical gym-floor methods to scientifically tested formulas. The Epley and Brzycki formulas were both derived from testing submaximal efforts across different populations. They allow trainers and lifters to program intelligently without the constant need to test true 1RM, which can be fatiguing or risky.</p>
            <p>These formulas became popular in the 80s and 90s and are now commonly implemented in strength training apps, spreadsheets, and certification programs. While not perfect, they offer a fast, low-risk way to anchor percentage-based programming.</p>
          </div>
        )}
      </div>

      {/* Input Card */}
      <div className="onerepmax-input-card">
        <h2>🏋️ Input Your Lift</h2>
        <div className="onerepmax-input-group">
          <label>Weight lifted (lbs or kg):</label>
          <input type="number" value={weight} onChange={(e) => setWeight(+e.target.value)} />
        </div>
        <div className="onerepmax-input-group">
          <label>Reps performed:</label>
          <input type="number" value={reps} onChange={(e) => setReps(+e.target.value)} />
        </div>
        <div className="onerepmax-input-group">
          <label>
            Formula:
            <span onClick={() => setShowFormulaInfo(!showFormulaInfo)} className="onerepmax-info-icon">ℹ️</span>
          </label>
          <select value={formula} onChange={(e) => setFormula(e.target.value)}>
            <option value="epley">Epley</option>
            <option value="brzycki">Brzycki</option>
          </select>
          {showFormulaInfo && (
            <div className="onerepmax-formula-info">
              <strong>Epley:</strong> Best used for moderate rep ranges (5–10). Simple and widely adopted.<br />
              <strong>Brzycki:</strong> Generally more accurate for low reps (&lt;10). May become unstable for high reps.
            </div>
          )}
        </div>
      </div>

      {/* Results Card */}
      <div className="onerepmax-results-card">
        <h2 className="onerepmax-one-rm-result">📈 Estimated One Rep Max: 
          <span>{oneRM.toFixed(1)}</span>
          </h2>

        <h3>📊 Rep Max Table</h3>
        <table className="onerepmax-rep-max-table">
          <thead>
            <tr><th>Reps</th><th>Estimated Max</th><th>% of 1RM</th></tr>
          </thead>
          <tbody>
            {[...Array(maxRepsToShow)].map((_, i) => {
              const r = i + 1;
              const est = estimateForReps(r, oneRM, formula);
              const pct = (est / oneRM) * 100;
              const highlight = r === 1 ? { backgroundColor: '#e6ffe6', fontWeight: 'bold' } : {};
              return (
                <tr key={r} className={r === 1 ? 'onerepmax-highlight-row' : ''}>
                  <td>{r}</td>
                  <td>{est.toFixed(1)}</td>
                  <td>{pct.toFixed(1)}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <button 
          onClick={() => setMaxRepsToShow(maxRepsToShow + 5)} 
          className="onerepmax-show-more-button"
        >
          Show Higher Rep Maxes
        </button>
      </div>

      {/* Disclaimer Card */}
      <div className="onerepmax-disclaimer-card">
        <strong>⚠️ Disclaimer:</strong> These formulas are estimates. Accuracy may vary depending on your training experience, fatigue levels, exercise type (e.g., compound vs isolation), and form. Always test maxes under supervision if safety is a concern.
      </div>

      {/* Formula Explanation Card */}
      <div className="formula-explanation-card">
        <h4>📚 Formula Descriptions</h4>
        <ul>
          <li><strong>Epley</strong>: 1RM = weight × (1 + reps / 30) — good for moderate rep ranges.</li>
          <li><strong>Brzycki</strong>: 1RM = weight × (36 / (37 - reps)) — more accurate for low reps (&lt;10).</li>
        </ul>
        <p style={{ fontSize: '0.85rem', color: '#ccc' }}>
          References:
          <ul>
            <li>Epley, B. (1985). <em>Weight Training: A Scientific Approach</em>.</li>
            <li>Brzycki, M. (1993). <em>A Practical Approach to Strength Training</em>.</li>
          </ul>
        </p>
      </div>

      {/* Footer */}
      <div className="onerepmax-footer">
        <button onClick={() => alert('Link copied!')} className="onerepmax-share-button">
          🔗 Share This Tool
        </button>
        <p>Powered by ThePersonalTrainersClub.com</p>
      </div>
    </div>
  );
}

export default OneRepMaxTool;
